define("Modules/CheckoutFooter", [`Module`, `module`], function (Super, Self) {
	return Super.inherit({
		namespace: Self.id,
		requires: [`Events`, `ReservedSeating`, `ShoppingCart`],
		defaults: {
			render: true,
			displayCheckoutWith0Items: false, // whether the checkout button should be displayed when there are no items in the cart
			displayRemoveForReserved: false, // whether removal of reserved items is possible
			displayRemoveForOpenSeating: true, // whether removal of open seating items is possible
			displayFollow: true,
			displayContinueShopping: false,
			displayCountdownClock: false,
			displayZoomButtons: true,
			mode: `standard`, // standard, upgrades - Used to get the proper data for each selection. Upgrades needs to use the price difference.
		},
		async prepare(setup) {
			const { data, Events } = this;
			const {
				eventId,
				displayCheckoutWith0Items,
				displayContinueShopping,
				displayCountdownClock,
				displayFollow,
				displayRemoveForOpenSeating,
				displayRemoveForReserved,
				displayZoomButtons,
				mode,
			} = setup;
			const event = await Events.get({ id: eventId });
			const { hiddenDetailsList } = event;
			const isFollowable = !this.includes(hiddenDetailsList, `FOLLOW`);

			this.assign(data, {
				displayCheckoutWith0Items,
				displayContinueShopping,
				displayCountdownClock,
				displayFollow,
				displayRemoveForOpenSeating,
				displayRemoveForReserved,
				displayZoomButtons,
				event,
				isFollowable,
				mode,
			});
		},
		constructor(setup) {
			const { container, data, Events } = this;
			const { displayFollow, event, isFollowable, mode } = data;
			const { id: eventId } = event;

			const promises = [
				this.create({
					namespace: `Modules/CheckoutButton`,
					setup: {
						name: `checkout-button`,
						hide: false,
						show: true,
						showQuantity: false,
						eventId,
						mode,
					},
				}),
			];

			if (isFollowable && displayFollow) {
				promises.push(
					this.create({
						namespace: `Modules/FollowButton`,
						setup: {
							name: `follow-button`,
							eventId,
						},
					})
				);
			}

			if (event.feeInclusive) {
				let message = `Price includes fees.`;

				if (event.orderLevelAdditionalFees > 0) {
					message += ` An additional order processing fee of ${Events.formatPrice({
						price: event.orderLevelAdditionalFees,
						currency: event.currencies[0],
						type: `currencyCompact`,
					})} may be added to each order.`;
				}

				if (event.hasSalesWithFlatShipping) {
					message += ` Any applicable shipping costs will be added at checkout.`;
				}

				this.create({
					namespace: `Modules/InformationMessage`,
					setup: {
						container: this.get(`[name="feeInclusiveMessage"]`, container),
						header: `Fees`,
						message,
						type: `neutral`,
					},
				});
			}

			promises.push(
				this.create({
					namespace: `Modules/CheckoutFooterPriceCodeSelector`,
					setup: {
						container: this.container,
						name: `price-code-selector`,
					},
				})
			);

			return Promise.all(promises);
		},

		ready(setup) {
			const { data, ShoppingCart } = this;
			const { displayCountdownClock } = data;

			return ShoppingCart.get({
				subscribe: true,
				then: (cart) => {
					this.reconcile(cart);

					if (displayCountdownClock) {
						const { instances } = this;
						const name = `simple-countdown`;
						const className = `countdown-present`;
						const countdown = instances[name];
						const cachedExpiration = data.expiration;
						let expiration;

						if (this.isObject(cart) && this.isInt(cart.expiration)) {
							expiration = cart.expiration;
						} else {
							expiration = null;
						}

						if (this.isInt(expiration)) {
							if (expiration !== cachedExpiration) {
								const timestamp = expiration;

								if (this.isObject(countdown)) {
									countdown.change({ timestamp });
								} else {
									const prefix = `Items held for`;
									const suffix = null;
									const mode = `hybrid`;
									const show = true;
									this.create({
										namespace: `Modules/SimpleCountdown`,
										setup: { name, mode, timestamp, prefix, suffix, show },
									});
								}
							} // else nothing to do

							this.addClass(className);
						} else {
							this.removeClass(className);
						}
					}
				},
			});
		},
		api: {
			reconcile: {
				value(cartResponse = null) {
					const { callbacks, data, promises } = this;
					const cart = cartResponse === null ? data.cart : cartResponse;
					const { onToggleDisplay } = callbacks;
					const { displayCheckoutWith0Items, displayContinueShopping } = data;
					const { quantityTransition } = promises;
					const { customPaymentSchedule, quantity } = cart;
					const continueShoppingElement = this.get(`[name="continue-shopping"]`);
					const checkoutContainer = this.get(`.checkout-container`);
					const headerQuantityElement = this.get(`.cart-details-header .quantity-value`);
					const toggleCartDetailsElement = this.get(`[action="toggle-cart-details"]`);
					const toggleQuantityElement = this.get(`.quantity-value`, toggleCartDetailsElement);
					const checkoutButton = this.instances[`checkout-button`];
					const followButton = this.instances[`follow-button`];
					const hasFollowButton = this.isObject(followButton);

					if (quantity > 0 || displayCheckoutWith0Items) {
						this.removeClass(`hide`);

						if (hasFollowButton) {
							followButton.hide();
						}

						this.show({ target: checkoutContainer, transition: `height` });

						if (displayContinueShopping) {
							this.show({ target: continueShoppingElement, transition: `height` });
						} else {
							this.hide({ target: continueShoppingElement });
						}
					} else {
						this.hide({ target: checkoutContainer });

						if (displayContinueShopping) {
							this.hide({ target: continueShoppingElement, transition: `height` });
						} else {
							this.hide({ target: continueShoppingElement });
						}

						if (hasFollowButton) {
							followButton.show({ transition: `height` });
							this.removeClass(`hide`);
						} else {
							this.addClass(`hide`);
						}
					}

					if (quantity === 0) {
						this.addClass(`disabled`, toggleCartDetailsElement);
					} else {
						this.removeClass(`disabled`, toggleCartDetailsElement);
					}

					if (displayContinueShopping) {
						this.addClass(`display-continue-shopping`);
					}

					if (this.isFunction(onToggleDisplay)) {
						const isFooterDisplayed = quantity > 0 || hasFollowButton || displayCheckoutWith0Items;

						onToggleDisplay(isFooterDisplayed);
					}

					if (this.isObject(customPaymentSchedule)) {
						checkoutButton.hide();
						this.hide({ selector: `[action="toggle-cart-details"]` });
					}

					data.cart = cart;
					this.updateMiniCartItems();

					// force quantity changes to run in sequence
					promises.quantityTransition = this.when({
						promise: quantityTransition,
						then: async () => {
							const { visibleQuantity } = data;
							let latestQuantity = data.cart.quantity;

							if (latestQuantity !== visibleQuantity) {
								await this.hide({ duration: 100, target: toggleQuantityElement, transition: `height` });

								latestQuantity = data.cart.quantity; // in case it's changed in the interim
								if (this.isObject(headerQuantityElement)) {
									headerQuantityElement.innerHTML = `${latestQuantity}`;
								}
								if (this.isObject(toggleQuantityElement)) {
									toggleQuantityElement.innerHTML = `${latestQuantity}`;
								}
								data.visibleQuantity = latestQuantity;

								await this.show({ duration: 200, target: toggleQuantityElement, transition: `height` });
							}
						},
					});
				},
			},

			updateSettings: {
				public: true,
				value(context, setup) {
					const { data } = this;
					const { displayCheckoutWith0Items } = setup;

					this.assign(data, {
						...data,
						displayCheckoutWith0Items,
					});
					this.reconcile();
				},
			},

			updateMiniCartItems: {
				value() {
					const { data } = this;
					const { cart, event, mode } = data;
					const { products, quantity, upgrades } = cart;
					const transition = `height`;
					let remainingElements = this.select(`[data-selection-id]`).value();

					if (mode === `upgrades`) {
						// TODO - include fees with upgrades???
						this.each(upgrades, (upgrade) => {
							if (!this.isObject(upgrade.selection)) {
								return;
							}

							const { selection, targetSelected } = upgrade;
							const { priceDifference } = targetSelected;
							const product = this.find(products, (product) => {
								return this.find(product.selections, (selection) => {
									return this.isEqual(selection, upgrade.selection);
								});
							});

							const thumbUrl = this.isObject(product) ? product.thumbUrl : null;
							const target = this.renderSelection({ ...selection, priceDifference }, thumbUrl);

							remainingElements = this.without(remainingElements, target);
						});
					} else {
						this.each(products, (product) => {
							const { quantity: productQuantity, selections, thumbUrl } = product;
							const activeSelections = this.filter(selections, { state: `ACTIVE` });

							if (productQuantity === 0) {
								return;
							}

							this.each(activeSelections, (selection) => {
								const selectionClone = this.clone(selection);
								if (event.feeInclusive) {
									selectionClone.priceBreakoutMessage = product.priceBreakoutMessage;

									if (selectionClone.priceLevelOptionId != null) {
										const priceLevelOption = this.find(product.priceLevels, {
											value: selectionClone.priceLevelOptionId,
										});

										if (this.isObject(priceLevelOption)) {
											selectionClone.priceBreakoutMessage = priceLevelOption.priceBreakoutMessage;
										}
									}
								}

								const target = this.renderSelection(selectionClone, thumbUrl);

								remainingElements = this.without(remainingElements, target);
							});
						});
					}
					this.each(remainingElements, (element) => {
						this.hide({
							remove: true,
							target: element,
							transition,
						});
					});

					if (quantity === 0) {
						this.addClass(`hide`, this.get(`.cart-details`));
						this.addClass(`hide`, this.get(`.cart-details-background`));
					}
				},
			},

			renderSelection: {
				value(selection, thumbUrl) {
					const { data, instances, Events, ReservedSeating } = this;
					const { cart, displayRemoveForOpenSeating, displayRemoveForReserved, displayZoomButtons, event, mode } = data;
					const { currency } = cart;
					const { pickASeat } = event;
					const { fees, id: selectionId, name, price, priceDifference } = selection;
					const listElement = this.get(`.list`);
					const isUpgradesMode = mode === `upgrades`;
					const freeLabel = isUpgradesMode ? `Equal Value` : null;
					const formattedPrice =
						priceDifference != null
							? Events.formatPriceDifference({ currency, freeLabel, priceDifference })
							: Events.formatPrice({ currency, price });
					const seatSummary = ReservedSeating.makeSeatSummary(selection);
					let target = this.get(`[data-selection-id="${selectionId}"]`);
					let summaryElement;
					let feeItemizationElement;

					this.each(fees, (fee) => {
						fee.formattedAmount = Events.formatPrice({
							price: fee.amount,
							currency,
							type: `currencyCompact`,
						});
					});

					if (this.isElement(target)) {
						this.get(`.name`, target).innerHTML = `${name}`;
						this.get(`.price`, target).innerHTML = `${formattedPrice}`;
						this.get(`.fees`, target).innerHTML = `${selection.priceBreakoutMessage}`;

						if (this.isString(thumbUrl, 1)) {
							this.get(`[name="item-image"]`, target).style.backgroundImage = `url(${thumbUrl})`;
						}
					} else {
						target = document.createElement(`li`);

						this.hide({ target });

						target.setAttribute(`data-selection-id`, selectionId);

						const isReserved = this.isString(selection.seat, 1);
						const isSeatMapHidden = pickASeat === `HIDDEN`;

						selection.seatSummary = seatSummary;
						selection.productName = name;
						selection.formattedPrice = formattedPrice;
						selection.imageUrl = thumbUrl;
						selection.displayZoomButton = displayZoomButtons && isReserved && !isSeatMapHidden;
						selection.displayRemoveButton = isReserved ? displayRemoveForReserved : displayRemoveForOpenSeating;

						this.render({
							data: selection,
							name: `selection`,
							target,
						});

						listElement.appendChild(target);
						this.show({ target });
					}

					instances[`price-code-selector`].handleSelectionAdded({
						selection,
						target,
					});

					summaryElement = this.get(`.summary`, target);

					if (this.isString(seatSummary, 1)) {
						summaryElement.innerHTML = seatSummary;
						this.show({ target: summaryElement });
					} else {
						this.hide({ target: summaryElement });
					}

					feeItemizationElement = this.get(`.fees`, target);

					if (this.isString(selection.priceBreakoutMessage, 1)) {
						this.show({ target: feeItemizationElement });
					} else {
						this.hide({ target: feeItemizationElement });
					}

					return target;
				},
			},

			toggleCartDetailsVisibility: {
				public: true,
				value(context, visible) {
					const hideClass = `hide`;
					const cartDetailsElement = this.get(`.cart-details`);
					const cartDetailsBackgroundElement = this.get(`.cart-details-background`);
					const isCartDetailsHidden = this.hasClass(hideClass, cartDetailsElement);

					if (visible || (typeof visible === `undefined` && isCartDetailsHidden)) {
						this.removeClass(hideClass, cartDetailsElement);
						this.removeClass(hideClass, cartDetailsBackgroundElement);
					} else {
						this.addClass(hideClass, cartDetailsElement);
						this.addClass(hideClass, cartDetailsBackgroundElement);
					}
				},
			},

			toggleCartDetailsBackgroundVisiblity: {
				public: true,
				value(context, visible) {
					const hideClass = `hide`;
					const cartDetailsBackgroundElement = this.get(`.cart-details-background`);
					const isCartDetailsBackgroundHidden = this.hasClass(hideClass, cartDetailsBackgroundElement);

					if (visible || (typeof visible === `undefined` && isCartDetailsBackgroundHidden)) {
						this.removeClass(hideClass, cartDetailsBackgroundElement);
					} else {
						this.addClass(hideClass, cartDetailsBackgroundElement);
					}
				},
			},
		},
		listeners: [
			{
				event: `click`,
				filter: `[action]`,
				handler(evt) {
					const { DOM, ShoppingCart, callbacks, data } = this;
					const { cart, event } = data;
					const { currentTarget: link } = evt;
					const action = link.getAttribute(`action`);

					switch (action) {
						case `toggle-cart-details`:
							this.toggleCartDetailsVisibility();

							break;
						case `remove`: {
							const wrapper = DOM.closest(link, `[data-selection-id]`);
							const { dataset } = wrapper;
							const { selectionId: selectionIdString } = dataset;
							const selectionId = this.parseInt(selectionIdString);

							ShoppingCart.removeProductSelections({ id: selectionId });
							break;
						}
						case `zoom`:
							if (this.isFunction(callbacks.zoom)) {
								const wrapper = DOM.closest(link, `[data-selection-id]`);
								const { dataset } = wrapper;
								const { selectionId: selectionIdString } = dataset;
								const selectionId = this.parseInt(selectionIdString);
								let seatLabel;
								let saleId;

								// find the matching sale
								this.find(cart.products, (product) => {
									const { selections } = product;
									const selection = this.find(selections, { id: selectionId });

									if (!this.isObject(selection)) {
										return;
									}

									saleId = selection.saleId;
									seatLabel = selection.seatLabel;
									return true; // break loop
								});

								// create the object that will be passed into the callback
								const result = { saleId, seatLabel, selectionId };

								// look for a matching collection, if applicable
								const { collectionConfiguration } = event;
								if (this.isObject(event.collectionConfiguration)) {
									this.find(collectionConfiguration.collections, (collection) => {
										const { id: collectionId, sales } = collection;
										const matchingSale = this.find(sales, { id: saleId });

										if (this.isObject(matchingSale)) {
											result.collectionId = collectionId;
											return true; // break loop
										}
									});
								}

								callbacks.zoom(result);
							}
							break;

						case `continue-shopping`:
							if (this.isFunction(callbacks.continueShopping)) {
								callbacks.continueShopping();
							}
							break;

						default:
							break;
					}
				},
			},
		],
	});
});

define("Modules/CheckoutFooter.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Modules/CheckoutFooter/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " <div name=\"feeInclusiveMessage\"></div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"footer-main\"> <a href=\"#\" name=\"continue-shopping\" action=\"continue-shopping\" class=\"button\" theme=\"GRAY-LIGHT\"> <p>Continue Shopping</p> </a> <div name=\"simple-countdown\"></div> <div name=\"follow-button\"></div> <div class=\"checkout-container\"> <a href=\"#\" action=\"toggle-cart-details\" class=\"button cart\" theme=\"BLUE\"> "
    + alias2(lookupProperty(helpers,"getIcon").call(alias1,"shopping-cart-new",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":333},"end":{"line":1,"column":364}}}))
    + " <div class=\"quantity-wrapper\"> <span class=\"quantity-value\"></span> </div> </a> <div name=\"checkout-button\"></div> </div> </div> <div class=\"cart-details-background hide hide-desktop\" action=\"toggle-cart-details\"></div> <div class=\"cart-details hide\"> <div class=\"cart-details-header\"> <h2>Your <span class=\"quantity-value\"></span> Items</h2> <a href=\"#\" action=\"toggle-cart-details\" class=\"button arrow\" theme=\"GRAY\"> "
    + alias2(lookupProperty(helpers,"getIcon").call(alias1,"arrow-down",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":784},"end":{"line":1,"column":808}}}))
    + " </a> </div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"event") : depth0)) != null ? lookupProperty(stack1,"feeInclusive") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":821},"end":{"line":1,"column":894}}})) != null ? stack1 : "")
    + " <ul class=\"list\"></ul> </div> <div class=\"gradient\"></div>";
},"useData":true});

this["Templates"]["Modules/CheckoutFooter/selection"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <a href=\"#\" action=\"zoom\">"
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"seat",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":344},"end":{"line":1,"column":362}}}))
    + "</a> ";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <a href=\"#\" action=\"remove\">"
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"close",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":431},"end":{"line":1,"column":450}}}))
    + "</a> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div name=\"item-image\" style=\"background-image: url('"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"imageUrl") : depth0), depth0))
    + "');\"></div> <div class=\"item-info\"> <div class=\"name\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"productName") : depth0), depth0))
    + "</div> <div class=\"summary\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"seatSummary") : depth0), depth0)) != null ? stack1 : "")
    + "</div> <div class=\"price\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"formattedPrice") : depth0), depth0)) != null ? stack1 : "")
    + "</div> <div class=\"fees\"> "
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"priceBreakoutMessage") : depth0), depth0)) != null ? stack1 : "")
    + " </div> </div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"displayZoomButton") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":292},"end":{"line":1,"column":374}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"displayRemoveButton") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":375},"end":{"line":1,"column":462}}})) != null ? stack1 : "");
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=CheckoutFooter.js.map